package com.trevorwiebe.timesheet.core.presentation.common

import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.setValue
import androidx.compose.ui.platform.LocalUriHandler
import kotlinx.coroutines.Job
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.dropWhile
import kotlinx.coroutines.flow.first
import kotlinx.coroutines.launch

/** A tab opened ahead of the link it will show. */
interface PendingTab {
    fun open(url: String)
    fun close()
}

/**
 * Opens a tab showing [message] right away, from the click. Browsers block tabs opened after
 * waiting on work (e.g. a server call), so on web the tab is opened first and pointed at the link
 * once it's ready. Null where a link can simply be opened later.
 */
expect fun openPendingTab(message: String): PendingTab?

/**
 * Returns a function to call from the click that starts preparing a link. It waits on [state]
 * itself rather than on recomposition, since a web page stops recomposing once the new tab is in
 * front. Opens the link once [url] is set, then reports [onOpened]; if preparing ends without a
 * link, the waiting tab closes.
 */
@Composable
fun <S> rememberLinkOpener(
    state: StateFlow<S>,
    url: (S) -> String?,
    isPreparing: (S) -> Boolean,
    onOpened: () -> Unit,
): () -> Unit {
    val uriHandler = LocalUriHandler.current
    val scope = rememberCoroutineScope()
    var waiting by remember { mutableStateOf<Job?>(null) }
    return {
        if (waiting?.isActive != true) {
            val pending = openPendingTab("Preparing PDF…")
            waiting = scope.launch {
                var opened = false
                try {
                    val done = state
                        .dropWhile { url(it) == null && !isPreparing(it) }
                        .first { url(it) != null || !isPreparing(it) }
                    url(done)?.let {
                        pending?.open(it) ?: uriHandler.openUri(it)
                        opened = true
                        onOpened()
                    }
                } finally {
                    if (!opened) pending?.close()
                }
            }
        }
    }
}
